import React from 'react';
import Botao from '../primitivos/Botao';

/**
 * Paginação "Anterior / Página X de Y / Próxima" (at-56, frente B).
 *
 * 🔴 **Recebe `totalItens` + `porPagina`, e NÃO `totalPaginas`.** É de propósito:
 * o defeito "Página 1 de 0" (spec 02 §2.7) nasce de cada tela calcular
 * `Math.ceil(total/limite)` por conta própria — a aba de rascunhos já protegia
 * com `Math.max(1, …)` e a lista de adesões não, no mesmo arquivo. Com o cálculo
 * aqui dentro, **nenhum consumidor consegue reintroduzir o defeito**.
 *
 * ⚠️ **Sempre visível, inclusive com uma página só** — como no protótipo, que
 * renderiza o rodapé sempre que a lista tem itens. Eu havia feito o componente
 * se esconder nesse caso ("navegação para lugar nenhum é ruído") e Victor notou
 * a falta em 09/09/2026: os botões desabilitados são a **confirmação** de que a
 * lista acabou ali, e o rodapé fixo evita a tela pular de altura ao paginar.
 * Quem decide se o rodapé existe é o consumidor, condicionando à lista não estar
 * vazia — mesma filosofia do `DataList`: o componente é burro.
 */

interface PropsPagination {
  /** 1-based, como as APIs do projeto. */
  pagina: number;
  totalItens: number;
  porPagina: number;
  aoTrocar: (pagina: number) => void;
  /** Trava os dois botões enquanto algo carrega. */
  ocupado?: boolean;
}

const Pagination: React.FC<PropsPagination> = ({
  pagina,
  totalItens,
  porPagina,
  aoTrocar,
  ocupado = false,
}) => {
  /* Nunca zero: "Página 1 de 0" é o que este componente existe para impedir. */
  const totalPaginas = Math.max(1, Math.ceil(totalItens / Math.max(1, porPagina)));

  const noComeco = pagina <= 1;
  const noFim = pagina >= totalPaginas;

  return (
    <nav
      /* `px-4 py-3` são as medidas do protótipo. */
      className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-px-4 tw-py-3"
      aria-label="Paginação"
    >
      <Botao
        variante="secundario"
        tamanho="sm"
        iconeEsquerda="voltar"
        disabled={noComeco || ocupado}
        onClick={() => aoTrocar(pagina - 1)}
      >
        Anterior
      </Botao>

      {/* `aria-live` para o leitor de tela anunciar a troca de página — sem isso
          o usuário de leitor clica em "Próxima" e nada é dito. */}
      <span className="tw-text-xs tw-text-texto-suave sm:tw-text-sm" aria-live="polite">
        Página <strong className="tw-text-texto-titulo">{pagina}</strong> de{' '}
        <strong className="tw-text-texto-titulo">{totalPaginas}</strong>
      </span>

      <Botao
        variante="secundario"
        tamanho="sm"
        iconeDireita="chevron-direita"
        disabled={noFim || ocupado}
        onClick={() => aoTrocar(pagina + 1)}
      >
        Próxima
      </Botao>
    </nav>
  );
};

export default Pagination;
